import {
  Badge,
  breakpoints,
  Card,
  Inline,
  type SignalVariantTypes,
  Stack,
  Text,
  textVariants,
  vars,
} from '@etg/wings';
import { Accordion, CarrierLogo } from '@eti/components';
import { css } from '@eti/styles';
import type { ReactElement } from 'react';
import { useDateFormat, useTranslation } from '@eti/providers';
import { constants, date } from '@eti/utils';
import RouteInfo from '../common/RouteInfo';
import type { TransformedProviderBookingType } from './models';

const cardStyles = css`
  border: 1px solid ${vars.colors.divider};
`;

const carrierLogoStyles = css`
  height: 24px;
  width: 24px;
`;

const dividerStyles = css`
  background-color: ${vars.colors.divider};
  height: 1px;
  width: 100%;
`;

const pnrDateInlineStyles = css`
  flex-direction: column;
  gap: 4px 16px;
  justify-content: space-between;

  @media (min-width: ${breakpoints._560}) {
    align-items: center;
    flex-direction: row;
  }

  @media (min-width: ${breakpoints._768}) {
    align-items: start;
    flex-direction: column;
  }

  @media (min-width: ${breakpoints._970}) {
    align-items: center;
    flex-direction: row;
  }
`;

const titleBadgeInlineStyles = css`
  flex-direction: column-reverse;
  gap: 8px 16px;
  justify-content: space-between;

  @media (min-width: ${breakpoints._560}) {
    flex-direction: row;
  }

  @media (min-width: ${breakpoints._768}) {
    flex-direction: column-reverse;
  }

  @media (min-width: ${breakpoints._970}) {
    flex-direction: row;
  }
`;

interface RefundCardProps {
  badge: {
    variant?: SignalVariantTypes;
    title: string;
  };
  children: ReactElement;
  providerBooking: TransformedProviderBookingType;
}

const RefundStatusCard = ({ badge, children, providerBooking }: RefundCardProps) => {
  const { t } = useTranslation();
  const { datetime } = useDateFormat();
  const isNotLinkedPNR = providerBooking.__typename === 'RefundCaseProviderBooking';

  return (
    <Card
      className={cardStyles}
      data-testid={`refund-status-card-${providerBooking.reference}`}
      role="listitem"
    >
      <Stack>
        {isNotLinkedPNR ? (
          <Stack>
            <Inline alignY="top" className={titleBadgeInlineStyles}>
              <Inline alignY="top" noWrap spacing={8}>
                <CarrierLogo
                  className={carrierLogoStyles}
                  data-testid={`refund-status-card-${providerBooking.reference}-carrier-logo`}
                  id={providerBooking.carrierCode}
                  imageAlt={providerBooking.carrier}
                />
                <Stack spacing={8}>
                  {providerBooking.bounds.map((providerBookingBound, index) => (
                    <RouteInfo
                      dataTestId="provider-booking-card"
                      key={`${providerBookingBound.origin}-${providerBookingBound.destination}-${index}`}
                      route={{
                        destination: {
                          cityName: providerBookingBound.destination,
                          cityCode: providerBookingBound.destinationCityCode,
                        },
                        origin: {
                          cityName: providerBookingBound.origin,
                          cityCode: providerBookingBound.originCityCode,
                        },
                      }}
                    />
                  ))}
                </Stack>
              </Inline>
              <Badge
                data-testid={`refund-status-card-${providerBooking.reference}-status`}
                variant={badge.variant}
              >
                {badge.title}
              </Badge>
            </Inline>
            <span className={dividerStyles} />
            <Inline className={pnrDateInlineStyles}>
              <Text
                className={constants.MOUSE_FLOW_CLASS_NAMES}
                data-testid={`refund-status-card-${providerBooking.reference}-provider-reference`}
              >
                {t('MyBookings.RefundStatus.ProviderReference', providerBooking.reference)}
              </Text>
              {providerBooking.lastUpdatedUTC && (
                <Text
                  data-testid={`refund-status-card-${providerBooking.reference}-last-updated-date`}
                >
                  {t(
                    'MyBookings.RefundStatus.LastUpdateDate',
                    date.format(providerBooking.lastUpdatedUTC, datetime),
                  )}
                </Text>
              )}
            </Inline>
          </Stack>
        ) : (
          <Stack spacing={8}>
            <Inline className={titleBadgeInlineStyles}>
              <Text
                className={constants.MOUSE_FLOW_CLASS_NAMES}
                data-testid={`refund-status-card-${providerBooking.reference}-provider-reference`}
                variant={textVariants._16_BOLD}
              >
                {t('MyBookings.RefundStatus.ProviderReference', providerBooking.reference)}
              </Text>
              <Badge
                data-testid={`refund-status-card-${providerBooking.reference}-status`}
                variant={badge.variant}
              >
                {badge.title}
              </Badge>
            </Inline>
          </Stack>
        )}
        <Accordion id="refund-status-accordion" shouldExpandOnTop spacing={16}>
          <Accordion.Header
            data-testid={`refund-status-card-${providerBooking.reference}-expansion-panel-button`}
            expandedHeader={<Text>{t('MyBookings.RefundStatus.HideInformation')}</Text>}
          >
            <Text>{t('MyBookings.RefundStatus.ShowInformation')}</Text>
          </Accordion.Header>
          <Accordion.Body>{children}</Accordion.Body>
        </Accordion>
      </Stack>
    </Card>
  );
};

export default RefundStatusCard;
